import { useMemo, useRef, useState } from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useMutation, useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import { addDays, format, parseISO } from 'date-fns';
import { toast } from 'sonner';
import { z } from 'zod';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { FilterChip } from '@/components/shared/FilterChip';
import { MetricStrip } from '@/components/shared/MetricStrip';
import { surfaceApiError } from '@/lib/api-error';
import { api } from '@/api/api';
import { meOptions } from '@/features/auth/api';
import { usePermission } from '@/features/auth/use-permission';
import {
  projectMembersOptions,
  projectRolesOptions,
  useProjectRoles,
} from '@/features/projects/api';
import { sprintsOptions } from '@/features/sprints/api';
import { taskStatusOptions, tasksListOptions, useTaskStatuses } from '@/features/tasks/api';
import { useTaskForm } from '@/features/tasks/task-form-context';
import { workloadKeys, workloadOptions } from '@/features/workload/api';
import { defaultWorkloadWindow, projectSpanWindow } from '@/features/workload/window';
import { buildWorkloadMetrics, currentWeekColumn } from '@/features/workload/metrics';
import {
  ROSTER_SORTS,
  ROSTER_SORT_LABEL,
  excludeViewers,
  filterRoster,
  sortRoster,
  type RosterSort,
} from '@/features/workload/roster';
import { OverloadAlert } from '@/features/workload/components/OverloadAlert';
import {
  WorkloadHeatmap,
  type WorkloadHeatmapHandle,
} from '@/features/workload/components/WorkloadHeatmap';
import { WorkloadLegend } from '@/features/workload/components/WorkloadLegend';
import {
  ContributionsDrawer,
  type ContributionsTarget,
} from '@/features/workload/components/ContributionsDrawer';
import { leaveKeys } from '@/features/leaves/api';
import { rangesOverlap } from '@/features/leaves/working-days';
import { timelineKeys } from '@/features/timeline/api';

// A lone ?member=x (hand-typed, or a serializer flattening a one-element
// array) still counts as a selection.
const toArray = (v: unknown): unknown => (v === undefined ? undefined : Array.isArray(v) ? v : [v]);

const workloadSearchSchema = z.object({
  from: z.string().optional(),
  to: z.string().optional(),
  /** Members shown in the heatmap; absent = everyone. */
  member: z.preprocess(toArray, z.array(z.string()).optional()),
  /** Row order; absent = name A–Z. `.catch` guards hand-edited URLs. */
  sort: z.enum(ROSTER_SORTS).optional().catch(undefined),
});

export type WorkloadSearch = z.infer<typeof workloadSearchSchema>;

export const Route = createFileRoute('/_authed/projects/$projectId/workload')({
  validateSearch: workloadSearchSchema,
  loaderDeps: ({ search }) => ({ from: search.from, to: search.to }),
  loader: async ({ context, params, deps }) => {
    // The window spans the whole project, derived from its sprints. Fetch
    // sprints first (usually warm — Timeline prefetches them), then seed the
    // workload query for that span. Explicit URL from/to still override.
    const sprints = await context.queryClient.ensureQueryData(sprintsOptions(params.projectId));
    const span = projectSpanWindow(sprints) ?? defaultWorkloadWindow();
    await Promise.all([
      context.queryClient.ensureQueryData(meOptions()),
      context.queryClient.ensureQueryData(projectMembersOptions(params.projectId)),
      context.queryClient.ensureQueryData(projectRolesOptions(params.projectId)),
      context.queryClient.ensureQueryData(taskStatusOptions(params.projectId)),
    ]);
    return context.queryClient.ensureQueryData(
      workloadOptions({
        project_id: params.projectId,
        from: deps.from ?? span.from,
        to: deps.to ?? span.to,
      }),
    );
  },
  component: WorkloadPage,
});

function WorkloadPage() {
  const { projectId } = Route.useParams();
  const search = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const queryClient = useQueryClient();
  const { openEdit } = useTaskForm();
  const heatmapRef = useRef<WorkloadHeatmapHandle>(null);

  const { data: sprints } = useSuspenseQuery(sprintsOptions(projectId));
  const { data: me } = useSuspenseQuery(meOptions());
  const { data: members } = useSuspenseQuery(projectMembersOptions(projectId));
  const { statusesById } = useTaskStatuses(projectId);
  const { roles } = useProjectRoles(projectId);
  const canManageMembers = usePermission(projectId, 'ManageMembers');

  const span = useMemo(() => projectSpanWindow(sprints) ?? defaultWorkloadWindow(), [sprints]);
  const from = search.from ?? span.from;
  const to = search.to ?? span.to;

  const { data: rawData } = useSuspenseQuery(
    workloadOptions({ project_id: projectId, from, to }),
  );
  // Viewers never get a heatmap row. The member filter and sort apply on top;
  // filtering once here keeps the metrics, overload alert and heatmap in
  // agreement with the rows on screen.
  const roster = useMemo(() => excludeViewers(rawData, members), [rawData, members]);
  const sort: RosterSort = search.sort ?? 'name_asc';
  const selectedMemberIds = useMemo(() => search.member ?? [], [search.member]);
  const roleRank = useMemo(() => new Map(roles.map((r, i) => [r.id, i])), [roles]);
  const data = useMemo(
    () =>
      sortRoster(
        selectedMemberIds.length > 0 ? filterRoster(roster, selectedMemberIds) : roster,
        sort,
        members,
        roleRank,
      ),
    [roster, selectedMemberIds, sort, members, roleRank],
  );
  const memberOptions = useMemo(
    () => sortRoster(roster, 'name_asc', members, roleRank).users.map((u) => ({
      value: u.user_id,
      label: u.display_name,
    })),
    [roster, members, roleRank],
  );
  const roleNames = useMemo(
    () => new Map(members.map((m) => [m.user_id, m.role_name])),
    [members],
  );

  // Replace rather than push: toggling a filter shouldn't flood history.
  const updateSearch = (patch: Partial<WorkloadSearch>) =>
    void navigate({ search: (prev) => ({ ...prev, ...patch }), replace: true });

  const metrics = useMemo(() => buildWorkloadMetrics(data), [data]);
  const todayInWindow = useMemo(() => currentWeekColumn(data) >= 0, [data]);

  // The contributions drawer explains one (user, week) cell. We resolve the
  // member's display name and the leaves overlapping that week from the
  // response so the drawer needn't refetch them.
  const [target, setTarget] = useState<ContributionsTarget | null>(null);
  const openCell = (userId: string, weekStart: string) => {
    const user = data.users.find((u) => u.user_id === userId);
    const weekEnd = format(addDays(parseISO(weekStart), 6), 'yyyy-MM-dd');
    const leaves = (user?.leaves ?? []).filter((l) =>
      rangesOverlap(l.start_date, l.end_date, weekStart, weekEnd),
    );
    setTarget({ userId, weekStart, displayName: user?.display_name ?? 'Member', leaves });
  };

  const invalidateLeaveAndLoad = () =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: leaveKeys.all }),
      queryClient.invalidateQueries({ queryKey: workloadKeys.all }),
      queryClient.invalidateQueries({ queryKey: timelineKeys.all }),
    ]);

  const deleteLeave = useMutation({
    mutationFn: (leaveId: string) => api.deleteLeave(leaveId),
    onSuccess: async (_data, leaveId) => {
      toast.success('Leave deleted');
      await invalidateLeaveAndLoad();
      // Drop the drawer's cached copy of the now-deleted leave.
      setTarget((prev) =>
        prev ? { ...prev, leaves: prev.leaves.filter((l) => l.id !== leaveId) } : prev,
      );
    },
    onError: surfaceApiError,
  });

  const canManageLeaveFor = (userId: string) => userId === me.id || canManageMembers;

  // Open the task editor in place. The contribution row only carries a subset
  // of the task, so we resolve the full TaskResponse from the (cached, project-
  // wide) task list before handing it to the shared editor — which is mounted
  // at the project route, so reassign/split happen without leaving Workload.
  const openTaskEditor = async (taskId: string) => {
    try {
      const tasks = await queryClient.ensureQueryData(tasksListOptions(projectId));
      const task = tasks.find((t) => t.id === taskId);
      if (task) {
        setTarget(null);
        openEdit(task);
      }
    } catch (err) {
      surfaceApiError(err);
    }
  };

  return (
    // Fills the AppShell's scroll area so the heatmap can take every row of
    // height left over; below its floor the page itself scrolls.
    <div className="flex h-full flex-col">
      <MetricStrip metrics={metrics} />
      <OverloadAlert data={data} onInspectWeek={openCell} />
      <div className="flex flex-wrap items-center gap-x-6 gap-y-2 border-b border-border bg-bg px-4 py-2">
        <div className="flex items-center gap-2">
          <FilterChip
            label="Member"
            options={memberOptions}
            value={selectedMemberIds}
            onChange={(next) => updateSearch({ member: next.length > 0 ? next : undefined })}
            emptyMessage="No members."
            className="rounded-md px-3 shadow-sm"
          />
          <Select
            value={sort}
            onValueChange={(next) =>
              updateSearch({ sort: next === 'name_asc' ? undefined : (next as RosterSort) })
            }
          >
            <SelectTrigger aria-label="Sort members" className="h-7 w-auto gap-1.5 text-xs">
              <span className="text-text-muted">Sort:</span>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {ROSTER_SORTS.map((key) => (
                <SelectItem key={key} value={key}>
                  {ROSTER_SORT_LABEL[key]}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <span className="text-sm font-medium tabular-nums text-text">
          {format(parseISO(from), 'MMM d, yyyy')} – {format(parseISO(to), 'MMM d, yyyy')}
        </span>
        <WorkloadLegend />
        <Button
          type="button"
          size="sm"
          variant="ghost"
          className="ml-auto"
          disabled={!todayInWindow}
          title={todayInWindow ? undefined : 'Today falls outside this window'}
          onClick={() => heatmapRef.current?.scrollToCurrentWeek()}
        >
          This week
        </Button>
      </div>
      <WorkloadHeatmap
        ref={heatmapRef}
        data={data}
        roleNames={roleNames}
        onCellOpen={openCell}
        className="min-h-64 flex-1"
      />

      <ContributionsDrawer
        projectId={projectId}
        target={target}
        onClose={() => setTarget(null)}
        onOpenTask={(taskId) => void openTaskEditor(taskId)}
        canManageLeave={target ? canManageLeaveFor(target.userId) : false}
        deletingLeaveId={deleteLeave.isPending ? deleteLeave.variables : undefined}
        onDeleteLeave={(leaveId) => deleteLeave.mutate(leaveId)}
        statusesById={statusesById}
      />
    </div>
  );
}
